import { FunctionComponent, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { CheckoutSummaryTranslations, CheckoutSummary as CheckoutSummaryReusable } from '@cdaas/reusable-components-daas';
import { Gutter, Headline } from '@gaiads/telia-react-component-library';

import {
  executeLogout,
  shopResetOrderResponseAndDelivery,
  shopResetProduct,
  shopSetOrderResp,
  shopClearOrderOnBehalfUser,
  shopClearOrderForOrganization,
} from '../../../../store/actions';
import { RootState } from '../../../../store/reducers';
import { getStorageItems, removeStorageItems } from '../../../../utils';
import { COUNTRY_CALLING_CODES } from '../../../../utils/constants';
import CheckoutProductCart from '../CheckoutProductCart/CheckoutProductCart';
import { DeliveryMethodOfOrder } from '../../../../types';
import { ProductOrder } from '@cdaas/api-clients/dist/order-openapi';

interface CheckoutSummaryProps {
  isMobile?: boolean;
}

const CheckoutSummary: FunctionComponent<CheckoutSummaryProps> = ({ isMobile }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t: tShop, i18n } = useTranslation('shop');
  const { t: tGlobal } = useTranslation('globalComponents');
  const order = useSelector((state: RootState) => state.shop.orderResponse);
  const deliveryMethod = useSelector((state: RootState) => state.shop.selectedDeliveryMethod);
  const orderingForOrganization = useSelector((state: RootState) => state.shop.orderForOrganization);
  const orderingOnBehalfUserData = useSelector((state: RootState) => state.shop.orderOnBehalfUser);

  useEffect(() => {
    if (!order && !deliveryMethod) {
      const [selectedDelivery, orderResp] = getStorageItems(['tSelectedDelivery', 'tOrderResp'], 'session', [true, true]);
      if (orderResp && selectedDelivery) dispatch(shopSetOrderResp(orderResp as ProductOrder, selectedDelivery as DeliveryMethodOfOrder));
    }

    const isOnBehalfOrder = orderingForOrganization || orderingOnBehalfUserData.status;

    return () => {
      dispatch(shopResetProduct());
      dispatch(shopResetOrderResponseAndDelivery());
      removeStorageItems(['tItem', 'tSelectedDelivery', 'tOrderResp'], 'session');
      if (isOnBehalfOrder) {
        removeStorageItems(['tDelivery'], 'session');
        dispatch(shopClearOrderOnBehalfUser());
        dispatch(shopClearOrderForOrganization());
      }
    };
  }, [dispatch, order, orderingForOrganization, orderingOnBehalfUserData.status, deliveryMethod]);

  const handleGoShopping = (): void => {
    history.push('/home');
  };

  const handleLogOut = (): void => {
    dispatch(executeLogout());
    history.push('/authentication/logout');
  };

  const translations: CheckoutSummaryTranslations = {
    approverIconTitle: tShop('checkout.summaryPage.approverIconTitle'),
    orderTitle: orderingOnBehalfUserData.status
      ? tShop('checkout.summaryPage.orderTitleOrderingOnBehalf', {
          fname: orderingOnBehalfUserData.userToOrderFor?.given_name,
          lname: orderingOnBehalfUserData.userToOrderFor?.family_name,
        })
      : tShop('checkout.summaryPage.orderTitle'),
    yourOrderText: tShop('checkout.summaryPage.yourOrderText'),
    orderExplanationText: tShop(
      orderingForOrganization ? 'checkout.summaryPage.orderExplanationTextOrderingForOrganization' : 'checkout.summaryPage.orderExplanationText',
    ),
    detailsTitle: tShop('checkout.summaryPage.detailsTitle'),
    employeeIconTitle: tShop('checkout.summaryPage.employeeIconTitle'),
    deliveryIconTitle: tShop('checkout.summaryPage.deliveryIconTitle'),
    ordererIconTitle: tShop('checkout.summaryPage.ordererIconTitle'),
    deliveryEstimate1: tShop(
      orderingForOrganization ? 'checkout.summaryPage.deliveryEstimate1Organization' : 'checkout.summaryPage.deliveryEstimate1User',
    ),
    deliveryEstimate2: tShop('checkout.summaryPage.deliveryEstimate2'),
    nextButton: tShop('checkout.summaryPage.continueButton'),
    backButton: tShop('checkout.summaryPage.logoutButton'),
    deliveryEstimateNotAvailable: tShop('checkout.deliveryPage.deliveryEstimateNotAvailable'),
    optionDelivered: tShop('checkout.deliveryPage.optionDelivered'),
    countries: {
      Finland: tGlobal('countries.Finland'),
      Norway: tGlobal('countries.Norway'),
    },
  };

  return (
    <>
      <Headline size={'md'}>{tShop('checkout.summaryPage.headline')}</Headline>
      <Gutter />
      {!!order && !!deliveryMethod && (
        <CheckoutSummaryReusable
          translations={translations}
          order={order}
          deliveryMethod={deliveryMethod}
          i18Language={i18n.language}
          countries={COUNTRY_CALLING_CODES}
          showApprover={false}
          showEmployee={orderingOnBehalfUserData.status}
          showDelivery={true}
          showOrderer={true}
          ProductCart={isMobile ? <CheckoutProductCart /> : undefined}
          handleNextClick={handleGoShopping}
          handleBackClick={handleLogOut}
        />
      )}
      <Gutter size="lg" />
    </>
  );
};

export default CheckoutSummary;
